import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  api,
  MachineryScope,
  type MachineryExportRequest,
  type MachineryScopeValue,
} from '../api/client'
import { requireAccessToken } from '../auth/msal'
import { useMachineryQueryParams } from '../hooks/useMachineryQueryParams'
import './MachineryExport.css'

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/** Per browser, per person. One operator's preferred columns must not change
 *  what a colleague receives. */
const STORED_COLUMNS = 'prarambh.machineryExport.columns'

/**
 * The machinery register as a spreadsheet.
 *
 * A page rather than an overlay, per this codebase's own rule: choosing twenty
 * columns is not a transient gesture, and as a route a configured export is a URL
 * that survives Back and can be sent to a colleague.
 *
 * **The filter rides in the query string in exactly the register's own shape**, so
 * `useMachineryQueryParams` reconstructs it here with no second parser. Ticked rows
 * arrive as `?ids=`.
 *
 * **Share is feature-detected, not user-agent sniffed.** `navigator.canShare` with
 * a real File is the only honest test, and where it is absent the button simply is
 * not rendered. ⚠️ Do NOT add a `wa.me` fallback: that link carries text only, and
 * a link back into this app is useless to anyone outside the organisation because
 * there is no guest access.
 */
export function MachineryExport() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [query] = useMachineryQueryParams()

  const ids = useMemo(() => {
    const raw = params.get('ids')
    if (!raw) return []
    return raw
      .split(',')
      .map((x) => Number(x.trim()))
      .filter((n) => Number.isFinite(n) && n > 0)
  }, [params])

  const [scope, setScope] = useState<MachineryScopeValue>(
    ids.length > 0 ? MachineryScope.Selected : MachineryScope.All,
  )

  const [chosen, setChosen] = useState<ReadonlySet<string> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [emailing, setEmailing] = useState(false)
  const [address, setAddress] = useState('')

  const columns = useQuery({
    queryKey: ['machinery-export-columns'],
    queryFn: () => api.getMachineryExportColumns(),
    staleTime: 30 * 60 * 1000,
  })

  const allKeys = useMemo(
    () => (columns.data ?? []).flatMap((g) => g.columns.map((c) => c.key)),
    [columns.data],
  )

  /*
      Restored from localStorage, then INTERSECTED with what the server currently
      offers — so a preference saved before a column was renamed cannot resurrect
      a dead key, and everything ticks on by default when there is nothing stored.
  */
  useEffect(() => {
    if (allKeys.length === 0 || chosen !== null) return

    try {
      const stored = localStorage.getItem(STORED_COLUMNS)

      if (stored) {
        const keys: string[] = JSON.parse(stored)
        const live = keys.filter((k) => allKeys.includes(k))
        setChosen(new Set(live.length > 0 ? live : allKeys))
        return
      }
    } catch {
      // A private window, cleared site data, or storage blocked outright. Fall
      // through to everything ticked, which is the right default anyway.
    }

    setChosen(new Set(allKeys))
  }, [allKeys, chosen])

  function setColumns(next: ReadonlySet<string>) {
    setChosen(next)
    try {
      localStorage.setItem(STORED_COLUMNS, JSON.stringify([...next]))
    } catch {
      // Not being able to remember the choice is not a reason to refuse it.
    }
  }

  const chosenKeys = useMemo(() => [...(chosen ?? new Set<string>())], [chosen])

  const request = useMemo<MachineryExportRequest>(
    () => ({
      scope,
      // Everything ticked means "all of them", and sending no list keeps that
      // true even if a column is added to the registry after this page loaded.
      columns: chosenKeys.length === allKeys.length ? null : chosenKeys,
      machineryIds: scope === MachineryScope.Selected ? ids : null,
      filter: scope === MachineryScope.Filter ? query : null,
    }),
    [scope, chosenKeys, allKeys.length, ids, query],
  )

  const count = useQuery({
    queryKey: ['machinery-export-count', request],
    queryFn: () => api.countMachineryExport(request),
    enabled: chosen !== null,
  })

  const download = useMutation({
    mutationFn: async () => {
      // At the top of the click, while the gesture's user activation is live.
      await requireAccessToken()
      return api.exportMachineryWorkbook(request)
    },
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName()
      a.click()
      // Or the blob is held for the life of the page.
      URL.revokeObjectURL(url)
      setNote(`${count.data?.rows ?? 0} machines downloaded.`)
    },
    onError: (e: Error) => setError(e.message),
  })

  const share = useMutation({
    mutationFn: async () => {
      await requireAccessToken()
      const blob = await api.exportMachineryWorkbook(request)
      const file = new File([blob], fileName(), { type: XLSX_MIME })
      await navigator.share({
        files: [file],
        title: 'Machinery export',
        text: `${count.data?.rows ?? 0} machines from Prarambh.`,
      })
    },
    onError: (e: Error) => {
      // Dismissing the share sheet rejects with AbortError. That is a choice, not
      // a failure, and showing an error for it would be wrong.
      if (e.name !== 'AbortError') setError(e.message)
    },
  })

  const email = useMutation({
    mutationFn: async () => {
      await requireAccessToken()
      return api.emailMachineryExport(address.trim(), request)
    },
    onSuccess: (r) => {
      setEmailing(false)
      setAddress('')
      setNote(`Sent — ${r.rows} machines, delivery #${r.emailDeliveryId}.`)
    },
    onError: (e: Error) => setError(e.message),
  })

  function fileName() {
    return `Prarambh-machinery-${new Date().toISOString().slice(0, 10)}.xlsx`
  }

  const canShare =
    typeof navigator !== 'undefined' &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ files: [new File([], 'x.xlsx', { type: XLSX_MIME })] })

  const busy = download.isPending || share.isPending || email.isPending
  const nothing = count.data?.rows === 0

  return (
    <div className="mexp">
      <button type="button" className="mexp__back" onClick={() => navigate('/machinery')}>
        ← Machinery
      </button>

      <header className="mexp__head">
        <h1>Export machinery</h1>
        <p>
          One sheet, the columns you tick. No expiry columns — the asset list carries
          no dates for plant.
        </p>
      </header>

      {error && (
        <p className="mexp__error" role="alert">
          {error}
        </p>
      )}
      {note && <p className="mexp__note">{note}</p>}

      <section className="mexp__block">
        <h2>Which machines</h2>

        <Scope
          on={scope === MachineryScope.All}
          onPick={() => setScope(MachineryScope.All)}
          label="All machines"
          hint="Everything on the register, whatever the register is filtered to."
        />

        <Scope
          on={scope === MachineryScope.Filter}
          onPick={() => setScope(MachineryScope.Filter)}
          label="Current filter"
          hint={describe(query)}
        />

        <Scope
          on={scope === MachineryScope.Selected}
          onPick={() => ids.length > 0 && setScope(MachineryScope.Selected)}
          disabled={ids.length === 0}
          label={`Ticked rows (${ids.length})`}
          hint={
            ids.length === 0
              ? 'Nothing is ticked. Tick rows on the register first.'
              : 'Exactly the rows you ticked, whatever the filter says now.'
          }
        />
      </section>

      <section className="mexp__block">
        <div className="mexp__blockhead">
          <h2>Columns</h2>
          <span>
            {chosenKeys.length} of {allKeys.length}
          </span>
          <div>
            <button type="button" className="mexp__link" onClick={() => setColumns(new Set(allKeys))}>
              All
            </button>
            <button type="button" className="mexp__link" onClick={() => setColumns(new Set())}>
              None
            </button>
          </div>
        </div>

        {columns.isPending && <p className="mexp__muted">Loading columns…</p>}

        {(columns.data ?? []).map((group) => {
          const keys = group.columns.map((c) => c.key)
          const allOn = keys.every((k) => chosen?.has(k))

          return (
            <div key={group.group} className="mexp__group">
              <label className="mexp__grouphead">
                <input
                  type="checkbox"
                  checked={allOn}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate = keys.some((k) => chosen?.has(k)) && !allOn
                    }
                  }}
                  onChange={() => {
                    const next = new Set(chosen ?? [])
                    for (const k of keys) {
                      if (allOn) next.delete(k)
                      else next.add(k)
                    }
                    setColumns(next)
                  }}
                />
                <b>{group.group}</b>
                <span>{keys.filter((k) => chosen?.has(k)).length}</span>
              </label>

              <div className="mexp__cols">
                {group.columns.map((c) => (
                  <label key={c.key} className="mexp__col">
                    <input
                      type="checkbox"
                      checked={chosen?.has(c.key) ?? false}
                      onChange={() => {
                        const next = new Set(chosen ?? [])
                        if (!next.delete(c.key)) next.add(c.key)
                        setColumns(next)
                      }}
                    />
                    {c.header}
                  </label>
                ))}
              </div>
            </div>
          )
        })}
      </section>

      <section className="mexp__out">
        <div className="mexp__summary">
          <b className="mono">{fileName()}</b>
          <span>
            {count.isPending
              ? 'counting…'
              : `${(count.data?.rows ?? 0).toLocaleString('en-IN')} rows × ${chosenKeys.length} columns`}
          </span>

          {/* Said BEFORE the button is pressed, rather than the file simply
              arriving short. */}
          {count.data?.truncated && (
            <span className="mexp__warn">
              Capped at {count.data.maxRows.toLocaleString('en-IN')} rows — narrow the
              filter to get the rest.
            </span>
          )}
        </div>

        {/* A sentence, not a workbook with a header row and no data. */}
        {nothing && (
          <p className="mexp__muted">Nothing matches that. Widen the filter, or tick some rows.</p>
        )}

        <div className="mexp__actions">
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || nothing || chosenKeys.length === 0}
            onClick={() => {
              setError(null)
              download.mutate()
            }}
          >
            {download.isPending ? 'Building…' : 'Download'}
          </button>

          <button
            type="button"
            className="btn"
            disabled={busy || nothing || chosenKeys.length === 0}
            onClick={() => setEmailing((v) => !v)}
          >
            Email…
          </button>

          {/* Feature-detected: on a desktop there is no share sheet, so there is
              no button rather than a button that fails. */}
          {canShare && (
            <button
              type="button"
              className="btn"
              disabled={busy || nothing || chosenKeys.length === 0}
              onClick={() => {
                setError(null)
                share.mutate()
              }}
            >
              Share
            </button>
          )}
        </div>

        {emailing && (
          <form
            className="mexp__email"
            onSubmit={(e) => {
              e.preventDefault()
              setError(null)
              email.mutate()
            }}
          >
            <input
              type="email"
              required
              value={address}
              placeholder="name@example.com"
              onChange={(e) => setAddress(e.target.value)}
              aria-label="Send to"
            />
            <button type="submit" className="btn btn--primary" disabled={busy || !address.trim()}>
              {email.isPending ? 'Sending…' : 'Send'}
            </button>
          </form>
        )}
      </section>
    </div>
  )
}

function Scope({
  on,
  onPick,
  label,
  hint,
  disabled,
}: {
  on: boolean
  onPick: () => void
  label: string
  hint: string
  disabled?: boolean
}) {
  return (
    <label className={`mexp__scope${disabled ? ' mexp__scope--off' : ''}`}>
      <input type="radio" checked={on} disabled={disabled} onChange={onPick} />
      <span>
        <b>{label}</b>
        <small>{hint}</small>
      </span>
    </label>
  )
}

/**
 * The filter in words.
 *
 * Generated from the request rather than typed by whoever built this screen — two
 * places wording the same filter is how a description ends up describing a
 * different export from the one attached.
 */
function describe(query: ReturnType<typeof useMachineryQueryParams>[0]): string {
  const parts: string[] = []

  if (query.search) parts.push(`matching "${query.search}"`)
  if (query.machineryCategoryId) parts.push('one category')
  if (query.siteName) parts.push(`at ${query.siteName}`)
  if (query.make) parts.push(`made by ${query.make}`)
  if (query.yearFrom || query.yearTo) {
    parts.push(`year ${query.yearFrom ?? '…'}–${query.yearTo ?? '…'}`)
  }
  if (query.hasPlate === true) parts.push('carrying a plate')
  if (query.hasPlate === false) parts.push('with no plate')
  if (query.needsAttention === true) parts.push('incomplete')

  return parts.length === 0
    ? 'No filter is set, so this is the same as all machines.'
    : `Machines ${parts.join(', ')}.`
}
